import { useEffect } from 'react';
import type { DecoratedFileCollection } from '@witmetrics/utils';
import {
  useActivePractice,
  useDispatch,
  useFileCollection,
} from '@witmetrics/data-store';
import { isEmpty } from '@witmetrics/utils/misc';
import { API } from '@/api';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { useString } from '@/hooks/useString';
import { showSuccessToaster } from '@/utils/toasters';
import { useInvalidFields } from '@/hooks/useInvalidFields';
import Dialog, {
  DefaultDialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/Dialog';
import TextField from '@/components/inputs/TextField';
import { classes, isDisabled, validateFields } from './utils';

type FileCollectionDialogProps = {
  parentFileCollectionId?: number;
  fileCollectionId?: number;
  onSave?: (fileCollection: DecoratedFileCollection) => void;
  onClose: () => void;
};

export default function FileCollectionDialog({
  parentFileCollectionId,
  fileCollectionId,
  onSave,
  onClose,
}: FileCollectionDialogProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const activePractice = useActivePractice();
  const fileCollection = useFileCollection(fileCollectionId);
  const [name, setName] = useString();
  const [description, setDescription] = useString();
  const { setInvalidFields, isValidField } = useInvalidFields();
  const [isSaving, toggleSaving] = useToggle();

  useEffect(() => {
    if (fileCollection) fillFields();
  }, [fileCollection]);

  const fillFields = () => {
    setName(fileCollection!.name);
    setDescription(fileCollection!.description || '');
  };

  const handleCreateCollection = async () => {
    const collection = await API.FileCollections.createNewFileCollection({
      name,
      description,
      parentFileCollectionId,
      practiceId: activePractice!.id,
    });
    dispatch.addFileCollections([collection]);
    showSuccessToaster('File collection created');
    return collection;
  };

  const handleUpdateCollection = async () => {
    const collection = await API.FileCollections.updateFileCollection(
      fileCollectionId!,
      {
        name,
        description,
      }
    );
    const decoratedCollection = {
      ...collection,
      childFiles: fileCollection!.childFiles,
      childFileCollections: fileCollection!.childFileCollections,
    };
    dispatch.addFileCollections([decoratedCollection]);
    showSuccessToaster('File collection updated');
    return decoratedCollection;
  };

  const handleSave = async () => {
    try {
      if (!activePractice) return;
      toggleSaving(true);
      const invalid = validateFields(name);
      if (!isEmpty(invalid)) return setInvalidFields(invalid);
      const collection = await (fileCollectionId
        ? handleUpdateCollection()
        : handleCreateCollection());
      toggleSaving(false);
      if (onSave) onSave(collection);
      onClose();
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error saving file collection');
    }
  };

  return (
    <Dialog className={classes.dialog} onClose={onClose}>
      <DialogTitle
        title={
          fileCollectionId ? 'Update file collection' : 'New file collection'
        }
        onClose={onClose}
      />
      <DialogBody className={classes.body}>
        <TextField
          error={!isValidField('name')}
          className={classes.input}
          label="Name"
          value={name}
          onChange={setName}
        />
        <TextField
          multiline
          className={classes.input}
          minRows={3}
          maxRows={5}
          label="Description (optional)"
          value={description}
          onChange={setDescription}
        />
      </DialogBody>
      <DefaultDialogActions
        disabled={isDisabled(name)}
        pending={isSaving}
        confirmLabel="Save"
        pendingLabel="Saving"
        onCancel={onClose}
        onConfirm={handleSave}
      />
    </Dialog>
  );
}
